// © Microsoft Corporation. All rights reserved.

import { FluentButton } from "@fluentui/web-components";
import { DOM, FASTElement, observable } from "@microsoft/fast-element";
import { keyCodeEscape } from "@microsoft/fast-web-utilities";
import { SettingsLocalizedStrings, SettingsTelemetryContext } from "./Interfaces";

export class Settings extends FASTElement {
    /** Flag to hide the market selector */
    @observable public hideMarketSelector: boolean;

    /** Localized strings object */
    @observable public localizedStrings: SettingsLocalizedStrings = {};

    /** Callback to navigate to "Personalize" */
    @observable public goToPersonalize?: () => void = () => {};

    /** Flag if the settings dialog should be visible */
    @observable public isSettingsDialogVisible = false;

    /** Language dropdown ref */
    @observable public languageDropdown: HTMLElement[];

    /** Settings telemetry context */
    @observable public telemetryContext: SettingsTelemetryContext = {};

    /** Close button ref */
    public closeButton: FluentButton;

    /**
     * Sets the settings dialog visible state
     * @param isSettingsDialogVisible new isSettingsDialogVisible value
     */
    public toggleSettingsDialog = (isSettingsDialogVisible: boolean): void => {
        this.isSettingsDialogVisible = isSettingsDialogVisible;

        if (isSettingsDialogVisible) {
            document.addEventListener("click", this.onOutsideClick);
            document.addEventListener("keydown", this.onKeydown);

            if (this.closeButton) {
                DOM.queueUpdate(() => this.closeButton.focus());
            }
        } else {
            document.removeEventListener("click", this.onOutsideClick);
            document.removeEventListener("keydown", this.onKeydown);
        }
    };

    /**
     * Callback for when the personalize settings button is clicked
     */
    public onPersonalizeClick = (): void => {
        this.toggleSettingsDialog(false);
        this.goToPersonalize();
    };

    /**
     * Click handler for when the user clicks outside of the settings dialog
     * @param event mouse event
     */
    private onOutsideClick = (event: MouseEvent): void => {
        if (event.target instanceof Settings || this.isPartOfLanguageDropdown(event)) {
            return;
        }

        this.toggleSettingsDialog(false);
    };

    /**
     * Handler for key down events
     * @param event keyboard event
     */
    private onKeydown = (event: KeyboardEvent): void => {
        if (event.keyCode === keyCodeEscape) {
            this.toggleSettingsDialog(false);
        }
    };

    /**
     * Helper to check if event target is part of the slotted language dropdown
     */
    private isPartOfLanguageDropdown(event: MouseEvent): boolean {
        return !!(this.languageDropdown && this.languageDropdown.length && this.languageDropdown[0].contains(event.target as Node));
    }
}